/** Timing and signal thresholds used by one detector instance */
export type VoiceActivityDetectorOptions = {
  /** Never stop after trailing silence before the recording is this old */
  minRecordingMs: number;
  /** Stop at this age even if the user is still speaking */
  maxRecordingMs: number;
  /** Stop with no transcript after this long without confirming any speech */
  noSpeechMs: number;
  /** Stop after confirmed speech is followed by this much continuous silence */
  trailingSilenceMs: number;
  /** Time between microphone samples */
  frameMs: number;
  /** Minimum volume for one sample to count as speech */
  speechRmsThreshold: number;
  /** Number of speech-like frames required to confirm that the user spoke */
  minSpeechFramesForUtterance: number;
  /** Minimum share of sound in speech frequencies for a sample to count */
  minimumVoiceScore: number;
  /** Lowest frequency whose energy contributes to the voice score */
  minimumVoiceFrequencyHz: number;
  /** Highest frequency whose energy contributes to the voice score */
  maximumVoiceFrequencyHz: number;
};

/**
 * Default voice-activity detection settings for automatically stopped recordings.
 *
 * A frame counts as speech only when it meets both the volume threshold and the
 * configured minimum share of energy in the 250–3,400 Hz speech band (35% by
 * default). The band starts above the fundamental of most voices on purpose:
 * car, road, and ventilation rumble concentrates below ~200 Hz, while speech
 * energy peaks in the first-formant region (~300–800 Hz), so even a deep voice
 * keeps most of its energy in band. A few speech-like frames, not necessarily
 * consecutive, confirm an utterance. Trailing silence then controls when to
 * stop. This keeps short answers such as "yes" valid without letting one loud
 * frame end the recording.
 */
export const DEFAULT_VOICE_ACTIVITY_OPTIONS: VoiceActivityDetectorOptions = {
  minRecordingMs: 900,
  // Keep Voice Mode turns bounded independently of the larger manual-dictation upload allowance
  maxRecordingMs: 300_000,
  noSpeechMs: 15_000,
  trailingSilenceMs: 2_000,
  frameMs: 30,
  speechRmsThreshold: 0.035,
  minSpeechFramesForUtterance: 3,
  minimumVoiceScore: 0.35,
  minimumVoiceFrequencyHz: 250,
  maximumVoiceFrequencyHz: 3_400,
};
